import { useEffect, useState } from "react";
import {
    Brain,
    Network,
    Sparkles,
    AlertTriangle,
    Check,
    X,
    RefreshCw,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

function band(v) {
    if (v >= 75) return "Solide";
    if (v >= 55) return "Mesuré";
    if (v >= 35) return "À consolider";
    return "Émergent";
}

function tone(v) {
    if (v >= 75) return "text-[#d4af37]";
    if (v >= 55) return "text-[#e6d2a1]";
    if (v >= 35) return "text-[#cfb787]/70";
    return "text-[#f4ecd8]/45";
}

export function AIIntelligence({ dossierId, partners }) {
    const [analysis, setAnalysis] = useState(null);
    const [suggestions, setSuggestions] = useState(null);
    const [busyA, setBusyA] = useState(false);
    const [busyS, setBusyS] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        cockpit
            .get(`/dossiers/${dossierId}/ai/analysis`)
            .then(({ data }) => setAnalysis(data.none ? null : data));
        cockpit
            .get(`/dossiers/${dossierId}/ai/partner-suggestions`)
            .then(({ data }) => setSuggestions(data.none ? null : data));
    }, [dossierId]);

    const runAnalysis = async () => {
        setBusyA(true);
        setErr("");
        try {
            const { data } = await cockpit.post(
                `/dossiers/${dossierId}/ai/analyze`
            );
            setAnalysis(data);
        } catch (e) {
            setErr(
                e?.response?.data?.detail ||
                    "Le moteur n'a pas pu finaliser l'analyse."
            );
        } finally {
            setBusyA(false);
        }
    };

    const runSuggestions = async () => {
        setBusyS(true);
        setErr("");
        try {
            const { data } = await cockpit.post(
                `/dossiers/${dossierId}/ai/suggest-partners`
            );
            setSuggestions(data);
        } catch (e) {
            setErr(
                e?.response?.data?.detail ||
                    "Le moteur n'a pas pu finaliser la suggestion."
            );
        } finally {
            setBusyS(false);
        }
    };

    const logOutcome = async (partner_id, outcome) => {
        await cockpit.post(`/dossiers/${dossierId}/ai/partner-outcome`, {
            partner_id,
            outcome,
            comment: "",
        });
    };

    const r = analysis?.result;
    const bands = r
        ? [
              { k: "Faisabilité", v: r.feasibility },
              { k: "Maturité", v: r.maturity },
              { k: "Cohérence financière", v: r.financial_coherence },
              { k: "Qualité documentaire", v: r.documentary_quality },
              { k: "Attractivité", v: r.attractiveness },
          ]
        : [];

    return (
        <div data-testid="ai-section" className="mt-20">
            {err && (
                <div
                    data-testid="ai-error"
                    className="mb-8 text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1"
                >
                    {err}
                </div>
            )}

            {/* ---------- ANALYSIS ---------- */}
            <div className="border-t border-white/[0.06] pt-12 mb-16">
                <div className="flex items-end justify-between mb-10 gap-4">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-3">
                            <Brain strokeWidth={1.2} size={14} />
                            Lecture stratégique IA · interne
                        </div>
                        <h2
                            className="font-serif text-[28px] text-[#f4ecd8]"
                            style={{ fontWeight: 400 }}
                        >
                            Intelligence dossier
                        </h2>
                    </div>
                    <button
                        type="button"
                        data-testid="ai-analyze-btn"
                        onClick={runAnalysis}
                        disabled={busyA}
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        <RefreshCw
                            strokeWidth={1.2}
                            size={12}
                            className={busyA ? "animate-spin" : ""}
                        />
                        {busyA
                            ? "Analyse en cours…"
                            : analysis
                              ? "Relancer l'analyse"
                              : "Lancer l'analyse"}
                    </button>
                </div>

                {!analysis ? (
                    <p className="text-[#f4ecd8]/40 text-[14px] italic max-w-2xl">
                        Aucune analyse stratégique n'a encore été produite
                        pour ce dossier. Le moteur lira les méta-données, la
                        synthèse et les notes internes pour produire une
                        lecture stratégique sobre — strictement réservée au
                        cabinet.
                    </p>
                ) : (
                    <div className="grid lg:grid-cols-12 gap-10">
                        {/* SCORE GLOBAL */}
                        <div className="lg:col-span-4 bg-white/[0.02] border border-white/[0.06] p-10">
                            <div className="overline text-[#d4af37]/70 mb-3">
                                Score global
                            </div>
                            <div
                                className="font-serif text-[80px] leading-none text-[#f4ecd8]"
                                style={{ fontWeight: 300 }}
                            >
                                {r.overall_score}
                            </div>
                            <div className={`mt-4 text-[11px] tracking-[0.28em] uppercase ${tone(r.overall_score)}`}>
                                {band(r.overall_score)} · confiance {r.confidence}%
                            </div>
                            <p className="mt-8 text-[13px] text-[#f4ecd8]/70 leading-[1.85] font-light italic">
                                {r.strategic_reading}
                            </p>
                        </div>

                        {/* BANDES */}
                        <div className="lg:col-span-8">
                            <div className="overline text-[#d4af37]/70 mb-6">
                                Axes d'analyse
                            </div>
                            <div className="space-y-5">
                                {bands.map((b) => (
                                    <div
                                        key={b.k}
                                        data-testid={`ai-band-${b.k}`}
                                        className="grid grid-cols-[180px_1fr_auto] gap-5 items-center"
                                    >
                                        <div className="text-[11px] tracking-[0.22em] uppercase text-[#f4ecd8]/55">
                                            {b.k}
                                        </div>
                                        <div className="h-[2px] bg-white/10 relative overflow-hidden">
                                            <div
                                                className="absolute inset-y-0 left-0 bg-[#d4af37] transition-all duration-1000"
                                                style={{ width: `${b.v?.value || 0}%` }}
                                            />
                                        </div>
                                        <div className={`text-[12px] tracking-wide ${tone(b.v?.value || 0)} w-20 text-right`}>
                                            {b.v?.value}/100 · {b.v?.label || band(b.v?.value || 0)}
                                        </div>
                                        {b.v?.comment && (
                                            <p className="col-span-3 text-[12px] text-[#f4ecd8]/55 leading-[1.7] -mt-2 pl-[185px]">
                                                {b.v.comment}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* RISKS + NEXT */}
                            <div className="mt-12 grid sm:grid-cols-2 gap-8">
                                <div>
                                    <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-2">
                                        <AlertTriangle strokeWidth={1.2} size={12} />
                                        Risques identifiés
                                    </div>
                                    <ul className="space-y-2">
                                        {(r.risks || []).map((x, i) => (
                                            <li
                                                key={i}
                                                className="text-[13px] text-[#f4ecd8]/75 leading-[1.7] border-l border-[#d4af37]/40 pl-3"
                                            >
                                                {x}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-2">
                                        <Sparkles strokeWidth={1.2} size={12} />
                                        Prochaines actions suggérées
                                    </div>
                                    <ul className="space-y-2">
                                        {(r.recommended_next_steps || []).map((x, i) => (
                                            <li
                                                key={i}
                                                className="text-[13px] text-[#f4ecd8]/75 leading-[1.7] border-l border-[#d4af37]/40 pl-3"
                                            >
                                                {x}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <p className="mt-10 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/35">
                                Analyse interne · jamais exposée au client · {new Date(analysis.created_at).toLocaleString("fr-FR")}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* ---------- PARTNER SUGGESTIONS ---------- */}
            <div className="border-t border-white/[0.06] pt-12 mb-16">
                <div className="flex items-end justify-between mb-10 gap-4">
                    <div>
                        <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-3">
                            <Network strokeWidth={1.2} size={14} />
                            Suggestions IA · cercle privé
                        </div>
                        <h2
                            className="font-serif text-[28px] text-[#f4ecd8]"
                            style={{ fontWeight: 400 }}
                        >
                            Partenaires compatibles
                        </h2>
                    </div>
                    <button
                        type="button"
                        data-testid="ai-suggest-btn"
                        onClick={runSuggestions}
                        disabled={busyS || partners.length === 0}
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        <RefreshCw
                            strokeWidth={1.2}
                            size={12}
                            className={busyS ? "animate-spin" : ""}
                        />
                        {busyS
                            ? "En réflexion…"
                            : suggestions
                              ? "Régénérer"
                              : "Lancer la suggestion"}
                    </button>
                </div>

                {partners.length === 0 ? (
                    <p className="text-[#f4ecd8]/40 text-[14px] italic">
                        Le répertoire est vide. Ajoutez des partenaires pour activer la suggestion.
                    </p>
                ) : !suggestions ? (
                    <p className="text-[#f4ecd8]/40 text-[14px] italic max-w-2xl">
                        Aucune suggestion encore produite. Le moteur évaluera
                        chaque partenaire du répertoire à l'aune du dossier —
                        secteur, géographie, ticket, instrument, historique
                        relationnel — pour proposer un cercle restreint.
                    </p>
                ) : (
                    <div className="grid gap-px bg-white/[0.06] border border-white/[0.06]">
                        {suggestions.result.matches.map((m) => (
                            <div
                                key={m.partner_id}
                                data-testid={`ai-match-${m.partner_id}`}
                                className="grid lg:grid-cols-[260px_80px_1fr_auto] items-center gap-5 bg-[#0b1220] p-6 hover:bg-white/[0.02] transition-colors duration-500"
                            >
                                <div className="min-w-0">
                                    <div
                                        className="font-serif text-[18px] text-[#f4ecd8] truncate"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {m.partner_name}
                                    </div>
                                    <div className={`mt-1 text-[10px] tracking-[0.28em] uppercase ${tone(m.compatibility)}`}>
                                        Compatibilité {m.band}
                                    </div>
                                </div>
                                <div
                                    className={`font-serif text-[28px] leading-none ${tone(m.compatibility)}`}
                                    style={{ fontWeight: 300 }}
                                >
                                    {m.compatibility}
                                </div>
                                <p className="text-[13px] text-[#f4ecd8]/75 leading-[1.75] italic">
                                    {m.rationale}
                                </p>
                                <div className="flex gap-2 justify-end">
                                    <button
                                        type="button"
                                        onClick={() => logOutcome(m.partner_id, "accepted")}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase border border-[#d4af37]/40 text-[#d4af37]/80 hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                                        title="Retenir cette piste"
                                    >
                                        <Check strokeWidth={1.4} size={11} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => logOutcome(m.partner_id, "rejected")}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase border border-white/15 text-[#f4ecd8]/45 hover:border-[#e89090]/60 hover:text-[#e89090] transition-all duration-500"
                                        title="Écarter cette piste"
                                    >
                                        <X strokeWidth={1.4} size={11} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {suggestions.result.summary && (
                            <div className="bg-[#0b1220] p-6 text-[12px] text-[#f4ecd8]/55 leading-[1.85] italic">
                                {suggestions.result.summary}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
